/**
 * Professional Services Marketplace
 * Certified architects, engineers, and consultants with portfolios and instant booking
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Skeleton,
  Badge,
  Tooltip,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ImageList,
  ImageListItem,
  ImageListItemBar
} from '@mui/material';
import {
  Engineering,
  Architecture,
  Build,
  School,
  Verified,
  Star,
  LocationOn,
  Phone,
  Email,
  WhatsApp,
  Event,
  Work,
  Timeline,
  TrendingUp,
  Download,
  Visibility,
  Message,
  Schedule,
  Person,
  Business,
  Assignment,
  CheckCircle,
  ExpandMore,
  PhotoLibrary,
  Description,
  Award
} from '@mui/icons-material';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import { formatCurrency, formatDate } from '../../utils/helpers';
import DialogCloseButton from '../../components/common/DialogCloseButton';

// Mock professionals data
const mockProfessionalsData = [
  {
    id: 1,
    name: 'Dr. Sarah Mukamuri',
    title: 'Senior Structural Engineer',
    company: 'Mukamuri Engineering Consultancy',
    description: 'Specialized in high-rise buildings and seismic design with 15+ years experience in Zimbabwe construction industry.',
    hourlyRate: 85,
    projectRate: 12000,
    experience: 15,
    completedProjects: 127,
    ongoingProjects: 8,
    availability: 'Available in 2 weeks',
    profileImage: '/api/placeholder/150/150',
    location: 'Harare',
    specializations: ['Structural Analysis', 'Seismic Design', 'High-rise Buildings', 'Industrial Structures'],
    certifications: ['EIZ Registered', 'SAICE Member', 'PhD Structural Engineering', 'ZBCA Approved'],
    languages: ['English', 'Shona', 'Ndebele'],
    rating: 4.9,
    reviewCount: 43,
    responseTime: '2 hours',
    category: 'Engineering',
    subcategory: 'Structural',
    isVerified: true,
    isPremium: true,
    isTopRated: true,
    portfolio: [
      {
        id: 1,
        title: 'Harare CBD Office Complex',
        description: '20-story mixed-use development',
        image: '/api/placeholder/300/200',
        value: 15000000,
        year: 2023,
        category: 'Commercial'
      },
      {
        id: 2,
        title: 'Chitungwiza Hospital Extension',
        description: 'Seismic retrofit and expansion',
        image: '/api/placeholder/300/200',
        value: 8000000,
        year: 2022,
        category: 'Healthcare'
      },
      {
        id: 3,
        title: 'Industrial Warehouse Complex',
        description: 'Steel frame industrial facility',
        image: '/api/placeholder/300/200',
        value: 5000000,
        year: 2023,
        category: 'Industrial'
      }
    ],
    services: [
      { name: 'Structural Design', rate: 85, unit: 'hour' },
      { name: 'Seismic Analysis', rate: 95, unit: 'hour' },
      { name: 'Project Management', rate: 120, unit: 'hour' },
      { name: 'Site Inspection', rate: 300, unit: 'visit' }
    ],
    reviews: [
      {
        id: 1,
        client: 'Harare City Council',
        rating: 5,
        comment: 'Exceptional structural design for our municipal building. Very professional.',
        date: '2023-11-15',
        project: 'Municipal Complex'
      },
      {
        id: 2,
        client: 'Delta Corporation',
        rating: 5,
        comment: 'Outstanding seismic analysis and cost-effective solutions.',
        date: '2023-10-28',
        project: 'Brewery Expansion'
      }
    ]
  },
  {
    id: 2,
    name: 'Architect James Mutindi',
    title: 'Principal Architect',
    company: 'Mutindi Architectural Studio',
    description: 'Award-winning architect specializing in sustainable residential and commercial design.',
    hourlyRate: 75,
    projectRate: 8500,
    experience: 12,
    completedProjects: 89,
    ongoingProjects: 5,
    availability: 'Available now',
    profileImage: '/api/placeholder/150/150',
    location: 'Bulawayo',
    specializations: ['Sustainable Design', 'Residential Architecture', 'Commercial Buildings', 'Interior Design'],
    certifications: ['ZBCA Registered', 'RIBA Associate', 'Green Building Certified', 'Urban Planning Diploma'],
    languages: ['English', 'Ndebele', 'Shona'],
    rating: 4.8,
    reviewCount: 67,
    responseTime: '1 hour',
    category: 'Architecture',
    subcategory: 'Residential',
    isVerified: true,
    isPremium: false,
    isTopRated: true,
    portfolio: [
      {
        id: 1,
        title: 'Eco-Friendly Residential Estate',
        description: '50-unit sustainable housing development',
        image: '/api/placeholder/300/200',
        value: 12000000,
        year: 2023,
        category: 'Residential'
      },
      {
        id: 2,
        title: 'Bulawayo Shopping Mall',
        description: 'Modern retail and entertainment complex',
        image: '/api/placeholder/300/200',
        value: 25000000,
        year: 2022,
        category: 'Commercial'
      }
    ],
    services: [
      { name: 'Architectural Design', rate: 75, unit: 'hour' },
      { name: 'Interior Design', rate: 65, unit: 'hour' },
      { name: 'Project Consultation', rate: 85, unit: 'hour' },
      { name: 'Site Planning', rate: 400, unit: 'visit' }
    ],
    reviews: [
      {
        id: 1,
        client: 'Sunshine Developers',
        rating: 5,
        comment: 'Brilliant sustainable design concepts. Highly recommended.',
        date: '2023-12-01',
        project: 'Green Residential Complex'
      }
    ]
  },
  {
    id: 3,
    name: 'Eng. Michael Chivero',
    title: 'Electrical Engineering Consultant',
    company: 'Chivero Power Solutions',
    description: 'Electrical systems design for industrial and commercial projects with renewable energy integration.',
    hourlyRate: 70,
    projectRate: 9500,
    experience: 10,
    completedProjects: 156,
    ongoingProjects: 12,
    availability: 'Available in 1 week',
    profileImage: '/api/placeholder/150/150',
    location: 'Harare',
    specializations: ['Power Systems', 'Renewable Energy', 'Industrial Automation', 'Solar Design'],
    certifications: ['EIZ Registered', 'IEEE Member', 'Solar PV Certified', 'Automation Specialist'],
    languages: ['English', 'Shona'],
    rating: 4.7,
    reviewCount: 89,
    responseTime: '3 hours',
    category: 'Engineering',
    subcategory: 'Electrical',
    isVerified: true,
    isPremium: true,
    isTopRated: false,
    portfolio: [
      {
        id: 1,
        title: 'Solar Power Plant',
        description: '5MW commercial solar installation',
        image: '/api/placeholder/300/200',
        value: 7500000,
        year: 2023,
        category: 'Renewable Energy'
      }
    ],
    services: [
      { name: 'Electrical Design', rate: 70, unit: 'hour' },
      { name: 'Solar System Design', rate: 85, unit: 'hour' },
      { name: 'Power Audit', rate: 500, unit: 'visit' },
      { name: 'Installation Supervision', rate: 95, unit: 'hour' }
    ],
    reviews: []
  },
  {
    id: 4,
    name: 'Quantity Surveyor Lisa Dube',
    title: 'Senior Quantity Surveyor',
    company: 'Dube Cost Management',
    description: 'Professional cost management and quantity surveying services for construction projects.',
    hourlyRate: 60,
    projectRate: 7500,
    experience: 8,
    completedProjects: 203,
    ongoingProjects: 15,
    availability: 'Available now',
    profileImage: '/api/placeholder/150/150',
    location: 'Gweru',
    specializations: ['Cost Estimation', 'Contract Administration', 'Value Engineering', 'Risk Management'],
    certifications: ['ZIQS Registered', 'RICS Associate', 'Project Management Certificate'],
    languages: ['English', 'Shona'],
    rating: 4.6,
    reviewCount: 124,
    responseTime: '1 hour',
    category: 'Quantity Surveying',
    subcategory: 'Cost Management',
    isVerified: true,
    isPremium: false,
    isTopRated: false,
    portfolio: [
      {
        id: 1,
        title: 'Hospital Construction',
        description: 'Complete cost management for 200-bed facility',
        image: '/api/placeholder/300/200',
        value: 18000000,
        year: 2023,
        category: 'Healthcare'
      }
    ],
    services: [
      { name: 'Cost Estimation', rate: 60, unit: 'hour' },
      { name: 'Bills of Quantities', rate: 8000, unit: 'project' },
      { name: 'Contract Administration', rate: 70, unit: 'hour' },
      { name: 'Final Account', rate: 5000, unit: 'project' }
    ],
    reviews: []
  }
];

const ProfessionalsMarketplace = () => {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [profileDialog, setProfileDialog] = useState(false);
  const [hireDialog, setHireDialog] = useState(false);
  const [portfolioDialog, setPortfolioDialog] = useState(false);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null);

  // Hire dialog state
  const [hireDetails, setHireDetails] = useState({
    serviceType: '',
    projectDescription: '',
    timeline: '',
    budget: '',
    meetingPreference: 'online',
    contactMethod: 'platform'
  });

  // Load professionals data
  useEffect(() => {
    const loadProfessionals = async () => {
      setLoading(true);
      setTimeout(() => {
        // TODO: Replace with real API call to /api/marketplace/professionals
        setProfessionals([]); // Empty - no mock data
        setLoading(false);
      }, 500);
    };

    loadProfessionals();
  }, []);

  // Professional Profile Dialog
  const ProfileDialog = () => (
    <Dialog
      open={profileDialog}
      onClose={() => setProfileDialog(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogCloseButton onClick={() => setProfileDialog(false)} />
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={selectedProfessional?.profileImage}
            sx={{ width: 60, height: 60 }}
          />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {selectedProfessional?.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {selectedProfessional?.title}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {selectedProfessional && (
          <Box>
            {/* Overview */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Professional Overview
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedProfessional.description}
                </Typography>

                {/* Specializations */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
                  Specializations
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {selectedProfessional.specializations.map((spec, index) => (
                    <Chip key={index} label={spec} variant="outlined" />
                  ))}
                </Box>

                {/* Portfolio Preview */}
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Recent Projects
                </Typography>
                <Grid container spacing={2}>
                  {selectedProfessional.portfolio.slice(0, 3).map((project) => (
                    <Grid item xs={4} key={project.id}>
                      <Card
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedPortfolioItem(project);
                          setPortfolioDialog(true);
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="120"
                          image={project.image}
                          alt={project.title}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {project.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {project.year} • {formatCurrency(project.value)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                <Button
                  variant="outlined"
                  onClick={() => setPortfolioDialog(true)}
                  sx={{ mt: 2 }}
                  startIcon={<PhotoLibrary />}
                >
                  View Full Portfolio
                </Button>
              </Grid>

              <Grid item xs={12} md={4}>
                {/* Key Stats */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Professional Stats
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Experience</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedProfessional.experience} years
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Completed Projects</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedProfessional.completedProjects}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Client Rating</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating value={selectedProfessional.rating} precision={0.1} size="small" readOnly />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedProfessional.rating}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Response Time</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedProfessional.responseTime}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Availability</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedProfessional.availability}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Services & Rates */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Services & Rates
                    </Typography>
                    
                    {selectedProfessional.services.map((service, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{service.name}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(service.rate)}/{service.unit}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => setProfileDialog(false)}>Close</Button>
        <Button
          variant="contained"
          onClick={() => {
            setProfileDialog(false);
            setHireDialog(true);
          }}
        >
          Hire Professional
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Hire Professional Dialog
  const HireDialog = () => (
    <Dialog
      open={hireDialog}
      onClose={() => setHireDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogCloseButton onClick={() => setHireDialog(false)} />
      <DialogTitle>
        Hire {selectedProfessional?.name}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Service Required</InputLabel>
              <Select
                value={hireDetails.serviceType}
                label="Service Required"
                onChange={(e) => setHireDetails({...hireDetails, serviceType: e.target.value})}
              >
                {selectedProfessional?.services.map((service, index) => (
                  <MenuItem key={index} value={service.name}>
                    {service.name} - {formatCurrency(service.rate)}/{service.unit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Project Description"
              value={hireDetails.projectDescription}
              onChange={(e) => setHireDetails({...hireDetails, projectDescription: e.target.value})}
              placeholder="Describe your project requirements, scope, and specific needs..."
            />
          </Grid>
          
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Timeline"
              value={hireDetails.timeline}
              onChange={(e) => setHireDetails({...hireDetails, timeline: e.target.value})}
              placeholder="e.g., 2 weeks, 1 month"
            />
          </Grid>
          
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Budget Range"
              value={hireDetails.budget}
              onChange={(e) => setHireDetails({...hireDetails, budget: e.target.value})}
              placeholder="e.g., $5,000 - $10,000"
            />
          </Grid>
          
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Meeting Preference</InputLabel>
              <Select
                value={hireDetails.meetingPreference}
                label="Meeting Preference"
                onChange={(e) => setHireDetails({...hireDetails, meetingPreference: e.target.value})}
              >
                <MenuItem value="online">Online Meeting</MenuItem>
                <MenuItem value="site">Site Visit</MenuItem>
                <MenuItem value="office">Office Meeting</MenuItem>
                <MenuItem value="phone">Phone Call</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Contact Method</InputLabel>
              <Select
                value={hireDetails.contactMethod}
                label="Contact Method"
                onChange={(e) => setHireDetails({...hireDetails, contactMethod: e.target.value})}
              >
                <MenuItem value="platform">Platform Messaging</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => setHireDialog(false)}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!hireDetails.serviceType || !hireDetails.projectDescription}
          onClick={() => {
            alert('Hiring request sent successfully!');
            setHireDialog(false);
          }}
        >
          Send Hiring Request
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Professional Card Component
  const ProfessionalCard = ({ professional }) => (
    <Card sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
      }
    }}>
      <CardContent sx={{ pb: 1 }}>
        {/* Header with photo and basic info */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Avatar
            src={professional.profileImage}
            sx={{ width: 64, height: 64 }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {professional.name}
              </Typography>
              {professional.isVerified && <Verified color="primary" sx={{ fontSize: 18 }} />}
              {professional.isPremium && <Star color="warning" sx={{ fontSize: 18 }} />}
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {professional.title}
            </Typography>
            
            <Typography variant="caption" color="text.secondary">
              {professional.company}
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'right' }}>
            <Chip
              label={professional.category}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Badges */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
          {professional.isTopRated && (
            <Chip label="Top Rated" size="small" color="warning" />
          )}
          {professional.isPremium && (
            <Chip label="Premium" size="small" color="primary" />
          )}
          <Chip 
            label={`${professional.experience}+ years`} 
            size="small" 
            variant="outlined" 
          />
        </Box>

        {/* Rating and stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Rating value={professional.rating} precision={0.1} size="small" readOnly />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {professional.rating}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({professional.reviewCount} reviews)
            </Typography>
          </Box>
        </Box>

        {/* Location and availability */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {professional.location}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {professional.availability}
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {professional.description}
        </Typography>

        {/* Specializations */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {professional.specializations.slice(0, 3).map((spec, index) => (
            <Chip key={index} label={spec} size="small" variant="outlined" />
          ))}
          {professional.specializations.length > 3 && (
            <Chip label={`+${professional.specializations.length - 3}`} size="small" variant="outlined" />
          )}
        </Box>

        {/* Key stats */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {professional.completedProjects}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Projects
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {professional.responseTime}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Response
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {formatCurrency(professional.hourlyRate)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Per Hour
            </Typography>
          </Grid>
        </Grid>
      </CardContent>

      {/* Actions */}
      <Box sx={{ p: 2, pt: 0, mt: 'auto' }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => {
                setSelectedProfessional(professional);
                setProfileDialog(true);
              }}
              size="small"
            >
              View Profile
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Person />}
              onClick={() => {
                setSelectedProfessional(professional);
                setHireDialog(true);
              }}
              size="small"
            >
              Hire Now
            </Button>
          </Grid>
        </Grid>

        {/* Contact options */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          <IconButton size="small" color="success">
            <WhatsApp />
          </IconButton>
          <IconButton size="small" color="primary">
            <Message />
          </IconButton>
          <IconButton size="small" color="info">
            <Email />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );

  return (
    <>
      <MarketplaceLayout
        category="professionals"
        title="Professional Services"
        description="Certified architects, engineers, and consultants. Portfolios, ratings, instant booking available."
        data={professionals}
        loading={loading}
        totalResults={professionals.length}
        customActions={[
          {
            icon: <Assignment />,
            name: 'My Contracts',
            action: () => console.log('View contracts')
          }
        ]}
      >
        {/* Professionals Grid */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(8)].map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={6} lg={4}>
                <Skeleton variant="rectangular" height={450} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {professionals.map((professional) => (
              <Grid item key={professional.id} xs={12} sm={6} md={6} lg={4}>
                <ProfessionalCard professional={professional} />
              </Grid>
            ))}
          </Grid>
        )}
      </MarketplaceLayout>

      {/* Dialogs */}
      <ProfileDialog />
      <HireDialog />
    </>
  );
};

export default ProfessionalsMarketplace;